import type { ReactNode } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listMonsterMoves, swapMoveSlots, forgetMove } from "@/lib/game/moves.functions";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Crown,
  ArrowDownWideNarrow,
  Gauge,
  Hourglass,
  Lock,
  Pin,
  Sparkles,
  Swords,
  Timer,
  Trash2,
  Zap,
} from "lucide-react";
// Zonen-Taktung und v2-Ehrlichkeit kommen aus dem gemeinsamen Modul — dieselbe
// Quelle, die das Lern-Popup und MovesPanel benutzen. Die Zeitregel selbst
// steht in combat-core/cast.ts und wird hier nirgends nachgerechnet.
import {
  STRENGTH_HINT,
  castTiming,
  de1,
  fmtStrength,
  v2Class,
  v2Hint,
  v2Suffix,
  zoneStrength,
} from "@/lib/game/move-display";
// Der eingebaute Basis-Angriff ist kein Katalog-Move und steht deshalb in
// KEINER `monster_moves`-Zeile. Seine Zahlen kommen direkt aus den Konstanten,
// die `buildMonsterSnapshot` in den Kampf legt — nicht aus der Anzeige.
import {
  INTRINSIC_BASIC_COOLDOWN_MS,
  INTRINSIC_BASIC_POWER,
  intrinsicBasicName,
} from "@/lib/game/battle-v2/snapshot";
import { CREATURE_TYPES, type CreatureType } from "@/lib/game/types";
import { MoveOfferDialog } from "@/components/game/MoveLearnedModal";
import { toast } from "sonner";

import { RouteError, RouteNotFound } from "@/components/game/RouteError";
import { PageSkeleton } from "@/components/game/PageSkeleton";
import { InfoTip } from "@/components/game/InfoTip";
import { Panel } from "@/components/game/Panel";
import { Pill, RarityTag, TypeBadge, TYPE_LABELS } from "@/components/game/Badges";
import { MicroLabel, PageHeader, ScreenContainer } from "@/components/game/ScreenKit";
import { MOVE_STAT_TIPS, MOVE_EFFECT_TIPS, RARITY_TIPS } from "@/lib/game/tooltips";

import { useTutorial } from "@/components/game/TutorialProvider";
import { tServerError } from "@/lib/i18n/server-error";
import { currentLang } from "@/lib/i18n/lang";

export const Route = createFileRoute("/_app/game/moves")({
  component: MovesManager,
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
});

type Effect = { kind: string; [k: string]: unknown };
type Rarity = "common" | "rare" | "epic" | "legendary";
type Move = {
  id: string;
  name: string;
  description: string;
  flavor: string;
  type: string | null;
  power: number;
  cooldown_seconds: number;
  rarity: Rarity;
  effect: Effect;
  is_ultimate?: boolean;
};
/**
 * Bewusst OHNE `accuracy`, `mastery_level`, `auto_cast_mode`, `auto_priority`
 * und `auto_conditions`: Der Server liefert diese Felder zwar mit, aber keins
 * davon wirkt im Zonenkampf (`buildMonsterSnapshot` liest weder `accuracy` noch
 * `mastery_level`; die Auto-Cast-Modi und die Prio-Sortierung gehören dem alten
 * Idle-Kampf in `combat.functions.ts`). Sie standen hier als tote Typfelder und
 * waren die Einladung, sie „mal eben" wieder anzuzeigen.
 */
type Slot = { slot: number; move: Move };

const RARITIES: readonly Rarity[] = ["common", "rare", "epic", "legendary"];
const CREATURE_TYPE_SET = new Set<string>(CREATURE_TYPES);

function asCreatureType(v: string | null | undefined): CreatureType | null {
  return v && CREATURE_TYPE_SET.has(v) ? (v as CreatureType) : null;
}

function asRarity(v: string | null | undefined): Rarity {
  return v && (RARITIES as readonly string[]).includes(v) ? (v as Rarity) : "common";
}

/**
 * ⚠️ `RarityTag` ohne `label` fällt auf seine fest deutschen Labels zurück
 * (`RARITY_LABELS` in `components/game/Badges.tsx`, nicht exportiert, nirgends
 * übersetzt) — in der englischen Fassung stünde „SELTEN" neben englischem
 * Text. Der Aufrufer bringt die Übersetzung deshalb mit, wie in
 * `_app.game.breeding.tsx`.
 * 📌 Gehört als i18n-Schlüssel nach `Badges.tsx` — gemeinsame Datei, steht als
 * offener Punkt im Ergebnis.
 */
const RARITY_LABELS: Record<Rarity, { de: string; en: string }> = {
  common: { de: "Gewöhnlich", en: "Common" },
  rare: { de: "Selten", en: "Rare" },
  epic: { de: "Episch", en: "Epic" },
  legendary: { de: "Legendär", en: "Legendary" },
};

function rarityLabel(rarity: Rarity): string {
  const b = RARITY_LABELS[rarity];
  return currentLang() === "en" ? b.en : b.de;
}

/**
 * Beschriftung der beiden Umsortier-Knöpfe.
 *
 * ⚠️ Sie hieß „Move nach oben/unten verschieben" (`moves.ariaUp`/`ariaDown`).
 * Das stimmte, solange die Slot-Karten untereinander standen; sie liegen aber
 * seit dem Umbau in einem Raster (`sm:grid-cols-2 lg:grid-cols-3`), in dem
 * Slot 2 RECHTS von Slot 1 liegt. Ein Screenreader-Nutzer hörte damit „nach
 * oben", während sich nichts nach oben bewegt.
 *
 * Die Ziel-Slot-Nummer ist die einzige Angabe, die in JEDEM Layout stimmt —
 * und sie steht ohnehin auf jeder Karte. Deshalb benennt der Knopf sein Ziel,
 * statt eine Richtung zu behaupten.
 *
 * Gibt es in der Richtung gar keinen Slot (der Knopf ist dann ohnehin
 * gesperrt), nennt die Beschriftung keine erfundene Nummer — Slot 0 existiert
 * nicht.
 *
 * 📌 Der Text liegt hier zweisprachig statt in `game.json`, weil die
 * i18n-Dateien nicht zu dieser Aufgabe gehören (gemeldet). Muster und
 * Vorbehalt sind dieselben wie in `_app.game.breeding.tsx`: Sobald es
 * `moves.ariaToSlot` in de+en gibt, wird das hier zu einem `t()`-Aufruf.
 */
function toSlotLabel(slot: number, maxSlots: number): string {
  const en = currentLang() === "en";
  if (slot < 1 || slot > maxSlots) {
    return en ? "No slot in that direction" : "Kein Slot in dieser Richtung";
  }
  return en ? `Move to slot ${slot}` : `Attacke in Slot ${slot} verschieben`;
}

function fmtCd(s: number): string {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r === 0 ? `${m}m` : `${m}m ${r}s`;
}

// de1 / castTiming / zoneStrength: siehe @/lib/game/move-display (Import oben).
// Standen bis zum Lern-Popup hier und im MovesPanel doppelt.

/**
 * Der Katalog-Cooldown ist im Zonenkampf NICHT die Wartezeit zwischen zwei
 * Treffern — deshalb ein eigener Tip statt `MOVE_STAT_TIPS.cooldown`, der
 * genau das behauptete.
 */
function cdTip(move: { power: number; cooldown_seconds: number; is_ultimate?: boolean }): string {
  const { castS, cooldownS } = castTiming(move);
  return (
    `Cooldown-Wert aus dem Attacken-Katalog. Im Zonenkampf ist das nicht die Wartezeit: ` +
    `dort holt die Attacke erst ${de1(castS)}s aus, trifft und kühlt danach ${de1(cooldownS)}s ab — ` +
    `ein Treffer alle ${de1(castS + cooldownS)}s (Kachel „Treffer alle"). Nicht addieren.`
  );
}

function castTip(move: { power: number; cooldown_seconds: number; is_ultimate?: boolean }): string {
  const { castS, cooldownS } = castTiming(move);
  return (
    `Im Zonenkampf ist jede Attacke ab Kampfstart sofort verfügbar, holt dann aber ${de1(castS)}s aus — ` +
    `der Treffer fällt erst am Ende dieser Zeit, danach kühlt sie ${de1(cooldownS)}s ab. ` +
    `Zwischen zwei Treffern liegen also ${de1(castS + cooldownS)}s. ` +
    `Die Kachel „CD (Katalog)" ist der Katalogwert — sie ist hier NICHT zusätzlich zu warten: ` +
    "bei kurzen Cooldowns ist die Ausholzeit bereits abgezogen. " +
    "Jede Attacke holt für sich aus — mehrere laufen gleichzeitig, nur dieselbe Attacke nicht doppelt. Selbst abbrechen kannst du ein Ausholen nicht. Manche Attacken reißen ein Ziel aber daraus: Was unterwegs war, verpufft, und die Abklingzeit läuft trotzdem an — das gilt für dich wie für den Gegner."
  );
}

function cycleTip(move: { power: number; cooldown_seconds: number; is_ultimate?: boolean }): string {
  const { castS, cooldownS } = castTiming(move);
  return (
    `Der echte Abstand zweier Treffer dieser Attacke im Zonenkampf: ${de1(castS)}s Ausholen ` +
    `+ ${de1(cooldownS)}s Abklingzeit = ${de1(castS + cooldownS)}s. Das ist die Zahl, mit der du ` +
    "planst — nicht der Katalog-Cooldown daneben."
  );
}

/**
 * Was die Slot-Nummer WIRKLICH bedeutet.
 *
 * `MOVE_STAT_TIPS.slot` behauptet „Slot 1–4" (Stand vor den 7 Slots) und sagt
 * nichts über den Auto-Cast. Beide Aussagen unten sind im Code belegt:
 * Freischaltstufen aus `SLOT_UNLOCK` (moves.functions.ts, hier über die
 * Server-Antwort `slotUnlocks`), Reihenfolge aus `pickAutoMove`
 * (combat-core/simulate.ts): erst der Slot-1-Move, danach der erste bereite
 * Move in Listenreihenfolge.
 */
function slotTip(slotUnlocks: Record<number, number>): string {
  const stufen = [4, 5, 6, 7]
    .map((s) => `Slot ${s} ab Lv ${slotUnlocks[s] ?? "?"}`)
    .join(", ");
  return (
    "Die Slot-Nummer ist deine Reihenfolge: Mit Auto-Cast feuert immer der niedrigste Slot, " +
    "der gerade bereit ist. Und weil dein Xenomon nur EINE Attacke gleichzeitig ausholen kann, " +
    "verdrängt eine schwache Attacke auf Slot 1 die starken dahinter — sie ist ständig bereit " +
    "und belegt den Kanal.\n\n" +
    "Wie viel das ausmacht, ist gemessen: Dieselben Attacken, nur anders sortiert, haben in " +
    "unseren Testkämpfen bis zu 41 Prozentpunkte Siegquote ausgemacht. Es gibt dabei keine " +
    'Faustregel „stark zuerst" — bei manchen Kits ist genau das schlechter. Probier es aus, ' +
    "die Pfeile auf jeder Karte tauschen den Platz.\n\n" +
    `Die Slot-Nummer steuert außerdem, ab welchem Level der Platz aufgeht (Slots 1–3 von ` +
    `Anfang an, ${stufen}). Selbst antippen kannst du im Kampf jede bereite Attacke, egal in ` +
    "welchem Slot sie steckt — die Reihenfolge gilt nur für den Auto-Cast."
  );
}

/**
 * Der Ein-Satz-Hinweis über den Karten.
 *
 * ⚠️ Warum sichtbar statt im Tooltip: Der Slot-Hinweis hing bis zum 10.09.2026
 * ausschließlich an der Karte eines LEEREN Slots. Wer alle Slots voll hatte —
 * also jeder ab Level 15 — bekam ihn nie zu sehen. Genau ab da ist die
 * Reihenfolge aber am wichtigsten, weil dann nichts mehr dazukommt, sondern
 * nur noch getauscht wird.
 */
const SLOT_ORDNUNG_HINWEIS =
  "Mit Auto-Cast feuert immer der niedrigste bereite Slot — und es holt nur EINE Attacke " +
  "gleichzeitig aus. Die Reihenfolge ist deshalb eine echte Entscheidung: Mit den Pfeilen auf " +
  "den Karten tauschst du Plätze.";

const BASIS_TIP =
  "Fest eingebauter Basis-Angriff. Er belegt keinen Slot, lässt sich nicht tauschen, nicht " +
  "vergessen und nicht verbessern — jedes Xenomon trägt ihn. Mit Auto-Cast feuert er, wenn " +
  "gerade keine deiner Slot-Attacken bereit ist; auf einer neuen Stage (Frontier) ist er der " +
  "Einzige, der von allein feuert, dort tippst du alles andere selbst. Er holt nie aus und zählt " +
  "nie für den Aktiv-Bonus, weil er von allein losgeht.";

/**
 * ⚠️ Umgedreht am 09.09.2026. Hier stand jahrelang das GEGENTEIL: „reine
 * Optik … im Zonenkampf zählt er nicht". Das galt bis zum 07.09.2026 — seither
 * entscheidet der Typ der ATTACKE über den Vorteil (`attackTypeFor` in
 * combat-core/simulate.ts, „Weg 2"), und nur der intrinsische Grundangriff
 * erbt den Typ seines Trägers.
 *
 * Genau deshalb trägt die Pille daneben jetzt auch die Typfarbe: Sie bedeutet
 * etwas. Ohne Prozentzahl im Text — die stand hier schon zweimal falsch,
 * während der Faktor sich änderte; im Kampf leitet die Kachel sie aus dem
 * Faktor ab.
 */
const MOVE_TYPE_TIP =
  "Der Typ dieser Attacke — er entscheidet im Kampf, ob sie im Vorteil zuschlägt. Nicht der Typ " +
  "deines Xenomons: Du kannst mit einer Attacke fremden Typs kontern, obwohl dein Xenomon selbst " +
  "im Nachteil steht. Neutrale Attacken sind immer gleich stark, ohne Vorteil und ohne Nachteil — " +
  "und Hilfs-Attacken ohne Schaden lösen nie einen aus. Wie viel es ausmacht, steht im Kampf an " +
  "der Kachel.";

/**
 * Steht auf der Basis-Karte. Seit dem 07.09.2026 gilt der Xenomon-Typ im Kampf
 * nur noch für den Grundangriff — jede Katalog-Attacke bringt ihren eigenen mit.
 */
const MONSTER_TYPE_TIP =
  "Der Typ deines Xenomons. Im Kampf zählt er noch für den Basis-Angriff — der schlägt mit dem " +
  "Typ seines Trägers zu und ist auch nach ihm benannt. Für jede gelernte Attacke entscheidet " +
  "dagegen ihr eigener Typ, nicht deiner. Außerdem lernt dein Xenomon fast nur Attacken seines " +
  "Typs oder neutrale.";

const BASIS_STRENGTH_TIP =
  "Der Bezugspunkt: Der Basis-Angriff ist per Definition ×1,0, jede Katalog-Attacke wird an ihm " +
  "gemessen. Eine Attacke mit ×2,5 macht gegen denselben Gegner also das 2,5-Fache seines Schadens.";

function effectLabel(e: Effect, t: (k: string, o?: Record<string, unknown>) => string): string {
  switch (e.kind) {
    case "damage": return t("moves.effect.damage");
    case "multi_hit": return `Multi-Hit ×${e.hits}`;
    case "buff_atk": return `+ATK ×${e.factor} · ${e.duration_s}s`;
    case "buff_def": return `+DEF ×${e.factor} · ${e.duration_s}s`;
    case "crit_up": return `+${Math.round(Number(e.bonus) * 100)}% Crit · ${e.duration_s}s`;
    case "heal": return t("moves.effect.heal", { pct: Math.round(Number(e.pct) * 100) });
    case "dot": return `${e.dmg} DMG/Tick · ${e.duration_s}s`;
    case "lifesteal": return `${Math.round(Number(e.pct) * 100)}% Lifesteal · ${e.duration_s}s`;
    case "stun": return t("moves.effect.stun", { duration: e.duration_s });
    case "accuracy_down": return t("moves.effect.accuracyDown", { hits: e.hits });
    default: return e.kind;
  }
}

function MovesManager() {
  useTutorial("moves_intro");
  const { t } = useTranslation("game");
  const qc = useQueryClient();
  const list = useServerFn(listMonsterMoves);
  const swap = useServerFn(swapMoveSlots);
  const forget = useServerFn(forgetMove);

  const { data, isLoading } = useQuery({
    queryKey: ["monster-moves"],
    queryFn: () => list(),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["monster-moves"] });
  };

  const swapMut = useMutation({
    mutationFn: (v: { slotA: number; slotB: number }) => swap({ data: v }),
    onSuccess: () => {
      invalidate();
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const forgetMut = useMutation({
    mutationFn: (slot: number) => forget({ data: { slot } }),
    onSuccess: () => {
      invalidate();
      toast.success(t("moves.toastForgotten"));
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const slots = ((data?.slots ?? []) as Slot[]).slice().sort((a, b) => a.slot - b.slot);
  const monsterLevel = data?.monsterLevel ?? 1;
  const monsterType = (data?.monsterType ?? "neon") as CreatureType;
  const maxSlots = data?.maxSlots ?? 7;
  const slotUnlocks = (data?.slotUnlocks ?? { 1: 1, 2: 1, 3: 1, 4: 7, 5: 12, 6: 15, 7: 30 }) as Record<number, number>;
  const slotIds = Array.from({ length: maxSlots }, (_, i) => i + 1);

  if (isLoading) return <PageSkeleton variant="grid" />;

  return (
    <ScreenContainer>
      {/* Zweiter Zugang zu einem offenen Lern-Angebot: Wer es nach dem Kampf
          weggeklickt hat, entscheidet hier. Ohne das bliebe es für immer offen
          und blockierte (Regel „nur eines gleichzeitig") jedes weitere. */}
      <MoveOfferDialog />

      <PageHeader
        icon={<Swords className="h-6 w-6 shrink-0 text-primary" strokeWidth={2.5} />}
        title={t("nav.moves")}
        sub={t("moves.subtitle")}
      />

      {/* Sichtbar, nicht im Tooltip — Begründung an SLOT_ORDNUNG_HINWEIS. */}
      <Panel tone="secondary" className="flex items-start gap-2.5 p-3">
        <ArrowDownWideNarrow
          className="mt-0.5 h-4 w-4 shrink-0 text-primary"
          strokeWidth={2.5}
          aria-hidden
        />
        <p className="text-[13px] leading-snug text-muted-foreground">
          {SLOT_ORDNUNG_HINWEIS}{" "}
          <InfoTip text={slotTip(slotUnlocks)}>
            <button type="button" className="underline underline-offset-2">
              Was das im Kampf ausmacht
            </button>
          </InfoTip>
        </p>
      </Panel>

      <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
        {/* Der eingebaute Basis-Angriff steht zuerst — genau wie im Kampf, wo
            seine Kachel vor den Slots liegt. Er kam auf dieser Seite bisher gar
            nicht vor, obwohl er die einzige Attacke ist, die JEDES Xenomon hat. */}
        <BasisCard type={monsterType} />

        {slotIds.map((s, idx) => {
          const unlockLevel = slotUnlocks[s] ?? 1;
          const isLocked = monsterLevel < unlockLevel;
          const isUltSlot = s === 7;
          const slot = slots.find((sl) => sl.slot === s);

          if (isLocked || !slot) {
            return (
              <EmptySlotCard
                key={s}
                ultimate={isUltSlot}
                locked={isLocked}
                title={
                  isLocked
                    ? isUltSlot
                      ? t("moves.slotLockedUlt")
                      : t("moves.slotLocked", { slot: s })
                    : isUltSlot
                      ? t("moves.slotLockedUlt")
                      : `Slot ${s}`
                }
                hint={
                  isLocked
                    ? isUltSlot
                      ? t("moves.ultUnlockHint", { level: unlockLevel })
                      : t("moves.slotUnlockHint", { level: unlockLevel })
                    : isUltSlot
                      ? "Slot frei — beim nächsten Aufstieg kann eine Ultimate fallen."
                      : "Leer — die nächste gelernte Attacke zieht hier ein."
                }
                slotTipText={slotTip(slotUnlocks)}
              />
            );
          }

          const move = slot.move;
          const isUlt = move.is_ultimate === true || isUltSlot;
          const rarity = asRarity(move.rarity);
          const moveType = asCreatureType(move.type);
          const { castS, cooldownS } = castTiming(move);
          const strength = zoneStrength(move.power);
          const cls = v2Class(move.effect);
          const suffix = v2Suffix(cls);
          const effectTip = [
            MOVE_EFFECT_TIPS[move.effect.kind] ?? MOVE_STAT_TIPS.effect,
            v2Hint(move.effect),
          ]
            .filter(Boolean)
            .join("\n\n");

          return (
            <Panel
              key={s}
              grid
              // Der Ultimate-Slot trägt als einziger den Boss-Ton samt Holo —
              // so hält es auch die Kit-Vorlage. Legendäre Katalog-Attacken
              // bekommen den Schimmer, aber nicht die Boss-Fläche.
              tone={isUlt ? "boss" : "default"}
              holo={isUlt || rarity === "legendary"}
              className="h-full"
              contentClassName="flex h-full flex-col gap-3 p-3.5"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="text-[19px] leading-tight [overflow-wrap:anywhere]">{move.name}</h3>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {isUlt ? (
                      <InfoTip text="Slot 7 ist der Ultimate-Platz. Ultimates holen fest 8 s aus und lassen sich nicht in einen anderen Slot schieben.">
                        <Pill style={{ background: "var(--boss)", color: "var(--ink)" }}>
                          <Crown className="h-3 w-3" strokeWidth={2.5} />
                          Ultimate
                        </Pill>
                      </InfoTip>
                    ) : (
                      <InfoTip text={RARITY_TIPS[rarity] ?? ""}>
                        <RarityTag rarity={rarity} label={rarityLabel(rarity)} />
                      </InfoTip>
                    )}
                    {/* ⚠️ Umgedreht am 09.09.2026. Hier stand eine bewusst
                        FARBLOSE Pille mit der Begründung, eine Typfarbe trüge
                        „eine Bedeutung, die es nicht gibt". Genau umgekehrt:
                        Seit dem 07.09.2026 entscheidet der Typ der Attacke über
                        den Vorteil — die Farbe bedeutet hier also etwas, und
                        zwar dasselbe wie der Punkt auf der Kampf-Kachel.
                        Neutrale Attacken bekommen eine eigene graue Pille statt
                        wie bisher gar keine: „neutral" ist die wichtigste
                        Kampf-Aussage einer solchen Attacke, kein fehlender Wert. */}
                    <InfoTip text={MOVE_TYPE_TIP}>
                      {moveType ? (
                        <span>
                          <TypeBadge type={moveType} /> ⓘ
                        </span>
                      ) : (
                        <Pill
                          style={{ background: "var(--secondary)", color: "var(--foreground)" }}
                        >
                          neutral ⓘ
                        </Pill>
                      )}
                    </InfoTip>
                  </div>
                </div>
                <MicroLabel className={`shrink-0 ${isUlt ? "text-boss" : ""}`}>
                  <InfoTip text={slotTip(slotUnlocks)}>
                    <span>Slot {s} ⓘ</span>
                  </InfoTip>
                </MicroLabel>
              </div>

              {/* Die vier Zahlen, die im Zonenkampf tatsächlich zählen. Der
                  Vergleichswert ersetzt eine Schadenszahl, die ohne die DEF des
                  Gegners niemand ehrlich ausrechnen kann (siehe STRENGTH_HINT). */}
              <div className="grid grid-cols-2 gap-2">
                <StatTile
                  icon={<Zap className="h-3 w-3" strokeWidth={2.5} />}
                  label="Zonen-Schaden"
                  value={strength !== null ? fmtStrength(strength) : "—"}
                  tone="damage"
                  tip={
                    strength !== null
                      ? `${STRENGTH_HINT}\n\nKatalog-Power dieser Attacke: ${move.power.toLocaleString("de-DE")}. ${MOVE_STAT_TIPS.power}`
                      : "Diese Attacke hat keine Power und richtet im Zonenkampf keinen direkten Schaden an."
                  }
                />
                <StatTile
                  icon={<Hourglass className="h-3 w-3" strokeWidth={2.5} />}
                  label="Ausholen"
                  value={`${de1(castS)}s`}
                  tip={castTip(move)}
                />
                <StatTile
                  icon={<Timer className="h-3 w-3" strokeWidth={2.5} />}
                  label="Treffer alle"
                  value={`${de1(castS + cooldownS)}s`}
                  tip={cycleTip(move)}
                />
                <StatTile
                  icon={<Clock className="h-3 w-3" strokeWidth={2.5} />}
                  label="CD (Katalog)"
                  value={fmtCd(move.cooldown_seconds)}
                  tone="muted"
                  tip={cdTip(move)}
                />
              </div>

              {/* Ehrlichkeit an der Stelle, an der der Spieler entscheidet:
                  Battle v2 setzt die meisten Katalog-Effekte gar nicht um
                  (v2Class). Die Warnung steht deshalb im Klartext neben dem
                  Effekt, nicht nur als Zeichen. */}
              <InfoTip text={effectTip} className="block">
                <div className="flex items-start gap-1.5 text-xs">
                  <Sparkles
                    className="mt-px h-4 w-4 shrink-0 text-muted-foreground"
                    strokeWidth={2.5}
                  />
                  <span
                    className={
                      suffix
                        ? "font-extrabold text-primary [overflow-wrap:anywhere]"
                        : "text-muted-foreground [overflow-wrap:anywhere]"
                    }
                  >
                    {effectLabel(move.effect, t)}
                    {suffix ? ` · ${suffix}` : ""}
                  </span>
                </div>
              </InfoTip>

              {move.description?.trim() ? (
                <p className="text-xs text-muted-foreground [overflow-wrap:anywhere]">
                  {move.description}
                </p>
              ) : null}
              {move.flavor?.trim() ? (
                <p className="line-clamp-2 text-[11px] italic text-muted-foreground/80 [overflow-wrap:anywhere]">
                  „{move.flavor}“
                </p>
              ) : null}

              <div className="mt-auto flex items-center justify-between gap-2 pt-1">
                {/* Die Pfeile folgen dem Layout: eine Spalte (< sm) stapelt die
                    Karten untereinander, ab `sm` liegen sie im Raster
                    nebeneinander. Deshalb hier hoch/runter, dort links/rechts —
                    die Beschriftung nennt in beiden Fällen den Ziel-Slot und
                    bleibt damit unabhängig vom Umbruch richtig. */}
                <div className="flex gap-1.5">
                  <Button
                    size="icon"
                    variant="secondary"
                    onClick={() => swapMut.mutate({ slotA: s, slotB: s - 1 })}
                    disabled={idx === 0 || isUlt || swapMut.isPending}
                    aria-label={toSlotLabel(s - 1, maxSlots)}
                    title={isUlt ? t("moves.ultSlotFixed") : toSlotLabel(s - 1, maxSlots)}
                  >
                    <ChevronUp className="sm:hidden" strokeWidth={2.5} aria-hidden />
                    <ChevronLeft className="hidden sm:block" strokeWidth={2.5} aria-hidden />
                  </Button>
                  <Button
                    size="icon"
                    variant="secondary"
                    onClick={() => swapMut.mutate({ slotA: s, slotB: s + 1 })}
                    disabled={
                      idx === slotIds.length - 1 ||
                      isUlt ||
                      (slotUnlocks[s + 1] ?? 1) > monsterLevel ||
                      swapMut.isPending
                    }
                    aria-label={toSlotLabel(s + 1, maxSlots)}
                    title={isUlt ? t("moves.ultSlotFixed") : toSlotLabel(s + 1, maxSlots)}
                  >
                    <ChevronDown className="sm:hidden" strokeWidth={2.5} aria-hidden />
                    <ChevronRight className="hidden sm:block" strokeWidth={2.5} aria-hidden />
                  </Button>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <InfoTip text={MOVE_STAT_TIPS.forget}>
                      <Button size="sm" variant="ghost" className="text-destructive">
                        <Trash2 strokeWidth={2.5} /> Vergessen
                      </Button>
                    </InfoTip>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{move.name} vergessen?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Diese Attacke wird dauerhaft aus diesem Slot entfernt. Du kannst neue
                        Attacken im Kampf lernen oder sie später erneut gewinnen.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => forgetMut.mutate(s)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Vergessen
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </Panel>
          );
        })}
      </div>
    </ScreenContainer>
  );
}

/**
 * Der intrinsische Grundangriff als Karte.
 *
 * Alle Zahlen stammen aus `battle-v2/snapshot.ts` (den Konstanten, die
 * `buildMonsterSnapshot` an letzter Listenposition anhängt), nicht aus einer
 * `monster_moves`-Zeile — die gibt es für ihn nicht. Deshalb trägt die Karte
 * auch keine Slot-Nummer und keine Knöpfe: es gibt hier nichts zu tauschen.
 */
function BasisCard({ type }: { type: CreatureType }) {
  const cycleS = INTRINSIC_BASIC_COOLDOWN_MS / 1000;
  return (
    <Panel
      grid
      tone="secondary"
      className="h-full"
      contentClassName="flex h-full flex-col gap-3 p-3.5"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-[19px] leading-tight [overflow-wrap:anywhere]">
            {intrinsicBasicName(type)}
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <InfoTip text={BASIS_TIP}>
              <Pill style={{ background: "var(--accent)", color: "var(--foreground)" }}>
                <Pin className="h-3 w-3" strokeWidth={2.5} />
                Fest eingebaut
              </Pill>
            </InfoTip>
            {/* Hier trägt die Typfarbe ihre echte Bedeutung: Das ist der Typ
                des XENOMONS — der, mit dem der Kernel den Typ-Vorteil rechnet.
                Ohne diese Einordnung läse sich die farbige Pille neben den
                neutralen Pillen der Attacken-Karten wie „nur der Basis-Angriff
                hat einen wirksamen Typ". */}
            <InfoTip text={MONSTER_TYPE_TIP}>
              <TypeBadge type={type} label={`${TYPE_LABELS[type]} ⓘ`} />
            </InfoTip>
          </div>
        </div>
        <MicroLabel className="shrink-0">
          <InfoTip text={BASIS_TIP}>
            <span>Kein Slot ⓘ</span>
          </InfoTip>
        </MicroLabel>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <StatTile
          icon={<Zap className="h-3 w-3" strokeWidth={2.5} />}
          label="Zonen-Schaden"
          value={fmtStrength(zoneStrength(INTRINSIC_BASIC_POWER) ?? 1)}
          tone="damage"
          tip={BASIS_STRENGTH_TIP}
        />
        <StatTile
          icon={<Hourglass className="h-3 w-3" strokeWidth={2.5} />}
          label="Ausholen"
          value="0,0s"
          tip="Der Basis-Angriff holt nie aus — er trifft in dem Moment, in dem er losgeht. Genau dafür ist er da: Er füllt die Lücken, in denen keine deiner Slot-Attacken bereit ist."
        />
        <StatTile
          icon={<Timer className="h-3 w-3" strokeWidth={2.5} />}
          label="Treffer alle"
          value={`${de1(cycleS)}s`}
          tip={`Fester Takt: alle ${de1(cycleS)}s ein Treffer, ohne Ausholen. Der Wert lässt sich nicht verbessern.`}
        />
        <StatTile
          icon={<Gauge className="h-3 w-3" strokeWidth={2.5} />}
          label="Power"
          value={INTRINSIC_BASIC_POWER.toLocaleString("de-DE", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
          tone="muted"
          tip="Feste Power des eingebauten Basis-Angriffs. Sie ist der Bezugswert für den Zonen-Schaden jeder anderen Attacke."
        />
      </div>

      <p className="text-xs text-muted-foreground [overflow-wrap:anywhere]">
        Jedes Xenomon trägt diesen Angriff von Anfang an. Er belegt keinen Slot, lässt sich nicht
        tauschen und nicht vergessen.
      </p>
      <p className="mt-auto pt-1 text-[11px] text-muted-foreground/80 [overflow-wrap:anywhere]">
        Auf einer neuen Stage feuert nur er von allein — deine Slot-Attacken tippst du dort selbst.
      </p>
    </Panel>
  );
}

/** Gesperrter oder leerer Slot. Kein Panel: hier ist nichts, das Tiefe bräuchte. */
function EmptySlotCard({
  ultimate,
  locked,
  title,
  hint,
  slotTipText,
}: {
  ultimate: boolean;
  locked: boolean;
  title: string;
  hint: string;
  slotTipText: string;
}) {
  return (
    <div
      className={`flex h-full min-h-[132px] flex-col justify-center rounded-lg border-2 border-dashed bg-card/40 p-3.5 ${
        ultimate ? "border-boss/50" : "border-border-soft"
      }`}
    >
      <div className="flex items-center gap-2">
        {locked ? (
          <Lock className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={2.5} />
        ) : ultimate ? (
          <Crown className="h-4 w-4 shrink-0 text-boss" strokeWidth={2.5} />
        ) : (
          <Sparkles className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={2.5} />
        )}
        <MicroLabel className={ultimate ? "text-boss" : undefined}>
          <InfoTip text={slotTipText}>
            <span>{title} ⓘ</span>
          </InfoTip>
        </MicroLabel>
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground [overflow-wrap:anywhere]">{hint}</p>
    </div>
  );
}

/**
 * Zahlen-Kachel im Ink-Fenster — die Form, in der das Design-Kit Kennzahlen
 * zeigt. Zahlen immer tabellarisch, damit sie zwischen den Karten untereinander
 * stehen.
 */
function StatTile({
  icon,
  label,
  value,
  tip,
  tone = "accent",
}: {
  icon: ReactNode;
  label: string;
  value: string;
  tip?: string;
  tone?: "accent" | "damage" | "muted";
}) {
  const toneClass =
    tone === "damage" ? "text-destructive" : tone === "muted" ? "text-muted-foreground" : "text-accent";
  const body = (
    <div className="h-full rounded-md bg-ink px-2.5 py-1.5">
      {/* Kein `truncate`: „Zonen-Schaden" ist ein deutsches Kompositum und
          würde auf schmalen Karten mitten im Wort abgeschnitten. Umbrechen darf
          es — die Kacheln einer Reihe strecken sich ohnehin auf gleiche Höhe. */}
      <div className="flex items-start gap-1 font-display text-[10px] uppercase leading-tight tracking-[0.1em] text-muted-foreground">
        {icon ? <span className="mt-px shrink-0">{icon}</span> : null}
        <span className="[overflow-wrap:anywhere]">
          {label}
          {tip ? " ⓘ" : ""}
        </span>
      </div>
      <div className={`ink-shadow mt-0.5 font-display text-lg leading-tight tabular-nums ${toneClass}`}>
        {value}
      </div>
    </div>
  );
  return tip ? (
    <InfoTip text={tip} className="block">
      {body}
    </InfoTip>
  ) : (
    body
  );
}
